import { CircleUserRound, Mail, MessageCircle, Phone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ImageTrail } from '../../../shared/components';
import { userService } from '../../../shared/services/userService';
import { useShop } from '../../../shared/store';
import type { Product } from '../../../shared/types';

type PublisherCard = {
  name: string;
  email?: string;
  phone?: string;
  altPhone?: string;
  books: Product[];
  profileUrl?: string;
  slug: string;
};

type PublisherDirectoryEntry = {
  profileUrl?: string;
  phone?: string;
};

const getInitials = (name: string): string => {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  const first = tokens[0]?.[0] ?? '';
  const last = tokens.length > 1 ? tokens[tokens.length - 1]?.[0] ?? '' : '';
  return `${first}${last}`.toUpperCase() || '?';
};

const normalizeLookupValue = (value?: string): string => value?.trim().toLowerCase() ?? '';

const slugifyPublisher = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'publisher';

const getPublisherName = (product: Product): string => {
  const name =
    product.publishedCompany?.trim() ||
    product.uploaderName?.trim() ||
    product.authorName?.trim() ||
    product.author?.trim() ||
    '';

  if (normalizeLookupValue(name) === 'publisher 1') {
    return product.uploaderName?.trim() || product.authorName?.trim() || product.author?.trim() || '';
  }

  return name;
};

const getPublisherEmail = (product: Product): string | undefined =>
  product.publisherEmail?.trim() || product.uploaderEmail?.trim();

const getPublisherPhone = (product: Product): string | undefined =>
  product.publisherPhone?.trim();

const getPublisherAltPhone = (product: Product): string | undefined =>
  product.publisherAltPhone?.trim();

const PublishersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { products, themeMode } = useShop();
  const isDark = themeMode === 'dark';

  const publishers = useMemo<PublisherCard[]>(() => {
    const map = new Map<string, PublisherCard>();

    products.forEach((product) => {
      const publisherName = getPublisherName(product);
      if (!publisherName) return;

      const key = normalizeLookupValue(publisherName);
      const existing = map.get(key);
      
      const publisherEmail = getPublisherEmail(product);
      const publisherPhone = getPublisherPhone(product);
      const publisherAltPhone = getPublisherAltPhone(product);
      
      if (existing) {
        if (!existing.email && publisherEmail) existing.email = publisherEmail;
        if (!existing.phone && publisherPhone) existing.phone = publisherPhone;
        if (!existing.altPhone && publisherAltPhone) existing.altPhone = publisherAltPhone;
        existing.books.push(product);
      } else {
        map.set(key, {
          name: publisherName,
          email: publisherEmail,
          phone: publisherPhone,
          altPhone: publisherAltPhone,
          books: [product],
          slug: slugifyPublisher(publisherName),
          profileUrl: product.coverUrl,
        });
      }
    });

    const result = [...map.values()].filter((publisher) => publisher.books.length > 0);
    
    // Final pass to ensure slugs are unique across the result set
    const seenSlugs = new Set<string>();
    result.forEach((p) => {
      let baseSlug = p.slug;
      let counter = 1;
      while (seenSlugs.has(p.slug)) {
        p.slug = `${baseSlug}-${counter}`;
        counter++;
      }
      seenSlugs.add(p.slug);
    });

    return result;
  }, [products]);

  const heroImages = useMemo(() => {
    const seen = new Set<string>();
    const images: string[] = [];

    publishers.forEach((publisher) => {
      const candidates = [
        publisher.profileUrl,
        ...publisher.books.map((book) => book.coverUrl),
      ].filter((value): value is string => Boolean(value?.trim()));

      candidates.forEach((value) => {
        const normalized = value.trim();
        if (seen.has(normalized)) {
          return;
        }

        seen.add(normalized);
        images.push(normalized);
      });
    });

    return images.slice(0, 18);
  }, [publishers]);

  const totalTitles = useMemo(
    () => publishers.reduce((sum, publisher) => sum + publisher.books.length, 0),
    [publishers],
  );

  const loading = products.length === 0;
  const pageClassName = isDark
    ? 'min-h-screen bg-[#02050b] text-white'
    : 'min-h-screen bg-[linear-gradient(180deg,#fff8f1_0%,#ffffff_26%,#fffaf5_100%)] text-slate-900';
  const heroShellClassName = isDark
    ? 'relative overflow-hidden border-b border-white/10 bg-[linear-gradient(135deg,#000000_0%,#05070d_40%,#0b1120_100%)] shadow-[0_26px_90px_-60px_rgba(0,0,0,0.7)]'
    : 'relative overflow-hidden border-b border-orange-100 bg-[linear-gradient(135deg,#fff6ed_0%,#ffffff_48%,#fff1e8_100%)] shadow-[0_26px_90px_-60px_rgba(15,23,42,0.18)]';
  const heroOverlayClassName = isDark
    ? 'absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.16),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(249,115,22,0.1),transparent_24%),linear-gradient(180deg,rgba(255,255,255,0.02),transparent_40%)]'
    : 'absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(251,146,60,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_28%)]';
  const heroTintClassName = isDark
    ? 'pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.96)_0%,rgba(2,6,12,0.9)_34%,rgba(4,8,15,0.82)_58%,rgba(0,0,0,0.95)_100%)]'
    : 'pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,248,241,0.985)_0%,rgba(255,255,255,0.965)_34%,rgba(255,255,255,0.88)_58%,rgba(255,246,237,0.955)_100%)]';
  const statCardClassName = isDark
    ? 'rounded-3xl border border-white/10 bg-white/[0.04] px-5 py-4 shadow-[0_24px_60px_-40px_rgba(0,0,0,0.72)] backdrop-blur'
    : 'rounded-3xl border border-orange-100 bg-white px-5 py-4 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.18)]';
  const sectionTitleClassName = isDark ? 'text-3xl font-black text-white' : 'text-3xl font-black text-slate-900';
  const sectionSubtitleClassName = isDark ? 'mt-3 text-sm leading-7 text-slate-400' : 'mt-3 text-sm leading-7 text-slate-600';
  const cardClassName = isDark
    ? 'group overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,#030303_0%,#070b12_100%)] text-left shadow-[0_24px_80px_-46px_rgba(0,0,0,0.95)] transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-[0_38px_110px_-52px_rgba(249,115,22,0.35)]'
    : 'group overflow-hidden rounded-[28px] border border-orange-100 bg-white/95 text-left shadow-[0_24px_70px_-44px_rgba(15,23,42,0.18)] transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-[0_38px_110px_-52px_rgba(249,115,22,0.24)]';
  const handleTextClassName = isDark ? 'text-[11px] text-slate-500' : 'text-[11px] text-slate-500';
  const bodyTextClassName = isDark ? 'text-slate-300' : 'text-slate-700';
  const subtleTextClassName = isDark ? 'text-slate-400' : 'text-slate-600';
  const emptyStateClassName = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={pageClassName}>
      <section className="py-0">
        <div className={heroShellClassName}>
            <div className={heroOverlayClassName} />

            {heroImages.length > 0 ? (
              <div className="absolute inset-0">
                <ImageTrail
                  items={heroImages}
                  variant={2}
                  maxOpacity={isDark ? 0.14 : 0.18}
                  className="h-full w-full"
                  imageClassName={
                    isDark
                      ? 'w-[130px] rounded-[22px] border border-white/10 bg-white/5 shadow-[0_20px_45px_-30px_rgba(0,0,0,0.7)] sm:w-[160px]'
                      : 'w-[130px] rounded-[22px] border border-orange-100/80 bg-white/85 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.28)] sm:w-[160px]'
                  }
                />
                <div className={heroTintClassName} />
              </div>
            ) : null}

            <div className="pointer-events-none relative z-10 mx-auto max-w-7xl px-6 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-16">
              <div className="max-w-3xl">
                <span
                  className={`inline-flex rounded-full px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${
                    isDark
                      ? 'border border-orange-500/30 bg-orange-500/10 text-orange-300'
                      : 'border border-orange-200 bg-orange-50 text-orange-700'
                  }`}
                >
                  Publisher Directory
                </span>
                <h1 className={`mt-5 text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {t('publishers.hero.title', { defaultValue: 'Our African Creators' })}
                </h1>
                <p className={`mt-4 max-w-2xl text-sm leading-7 sm:text-base ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  {t('publishers.hero.subtitle', {
                    defaultValue: 'Meet the talented authors, illustrators, and publishers bringing African stories to life',
                  })}
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <div className={statCardClassName}>
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Publishers</p>
                    <p className={`mt-1 text-2xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{publishers.length}</p>
                  </div>
                  <div className={statCardClassName}>
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Titles</p>
                    <p className={`mt-1 text-2xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{totalTitles}</p>
                  </div>
                  <div className={statCardClassName}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-orange-500">Focus</p>
                    <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-orange-200' : 'text-orange-900'}`}>
                      Discover trusted creators and publishing houses in one place.
                    </p>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 pt-10">
        <div className="mb-8">
          <h2 className={sectionTitleClassName}>
            {t('publishers.section.title', { defaultValue: 'Our Publishers & Creators' })}
          </h2>
          <p className={sectionSubtitleClassName}>
            {t('publishers.section.subtitle', {
              defaultValue: 'Connect with the creative minds behind our collection of African literature',
            })}
          </p>
        </div>

        {loading && (
          <div className={`flex justify-center py-12 text-sm font-semibold ${emptyStateClassName}`}>
            {t('publishers.loading', { defaultValue: 'Loading publishers...' })}
          </div>
        )}

        {!loading && publishers.length === 0 && (
          <div className={`py-12 text-center ${emptyStateClassName}`}>
            {t('publishers.none', { defaultValue: 'No publishers found.' })}
          </div>
        )}

        {!loading && publishers.length > 0 && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {publishers.map((publisher) => (
              <button
                key={publisher.slug}
                type="button"
                onClick={() => navigate(`/publishers/${publisher.slug}`)}
                className={cardClassName}
              >
                <div className="p-5">
                  <div
                    className={`mb-4 flex items-start gap-3 rounded-[22px] p-3 ${
                      isDark ? 'bg-white/[0.03] ring-1 ring-white/5' : 'bg-orange-50/60 ring-1 ring-orange-100'
                    }`}
                  >
                    {publisher.profileUrl ? (
                      <img
                        src={publisher.profileUrl}
                        alt={publisher.name}
                        className={`h-14 w-14 rounded-2xl object-cover ${
                          isDark
                            ? 'border border-orange-500/25 bg-orange-950/20'
                            : 'border border-orange-100 bg-white'
                        }`}
                      />
                    ) : (
                      <div
                        className={`grid h-14 w-14 place-items-center rounded-2xl ${
                          isDark ? 'bg-orange-500/10 ring-1 ring-orange-500/20' : 'bg-white ring-1 ring-orange-100'
                        }`}
                      >
                        <CircleUserRound className="h-9 w-9 text-orange-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-orange-500">Publisher</p>
                      <h3 className={`mt-1 line-clamp-2 text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{publisher.name}</h3>
                      <p className={`mt-1 truncate ${handleTextClassName}`}>@{publisher.name}</p>
                      <p className={`mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        {t('publishers.titles', { defaultValue: 'Titles:' })} {publisher.books.length}
                      </p>
                    </div>
                  </div>

                  <div className={`mb-4 space-y-3 rounded-[22px] p-4 ${isDark ? 'bg-black/40 ring-1 ring-white/5' : 'bg-white ring-1 ring-orange-100/80'}`}>
                    <div className="flex items-start gap-2">
                      <Mail className="h-4 w-4 text-orange-500" />
                      <span className={`break-all whitespace-normal text-xs font-medium ${bodyTextClassName}`}>
                        {publisher.email ?? t('publishers.emailMissing', { defaultValue: 'Email not provided' })}
                      </span>
                    </div>

                    {publisher.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-orange-500" />
                        <span className={`text-xs font-semibold ${bodyTextClassName}`}>{publisher.phone}</span>
                      </div>
                    )}

                    {publisher.altPhone && !publisher.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-orange-500" />
                        <span className={`text-xs font-semibold ${bodyTextClassName}`}>{publisher.altPhone}</span>
                      </div>
                    )}

                    {(publisher.phone || publisher.altPhone) && (
                      <div className="flex gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold ${
                            isDark ? 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/20' : 'bg-green-50 text-green-700'
                          }`}
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          {t('publishers.whatsappReady', { defaultValue: 'WhatsApp ready' })}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className={`flex items-center justify-between border-t pt-3 ${isDark ? 'border-white/10' : 'border-orange-100'}`}>
                    <span className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${subtleTextClassName}`}>
                      View profile
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                        isDark ? 'bg-orange-500/10 text-orange-300 ring-1 ring-orange-500/20' : 'bg-orange-50 text-orange-700'
                      }`}
                    >
                      Open
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default PublishersPage;
